import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledMentionList from 'components/molecules/Mentions/MentionList/MentionList.styles';
import MenuList from 'components/atoms/Menus/MenuList/MenuList';
import MenuItem from 'components/atoms/Menus/MenuItem/MenuItem';

const MentionList = React.forwardRef((props, ref) => {
  const {
    items,
    command,
    open,
    ...innerProps
  } = useComponentProps(props, 'MentionList');

  const innerRef = useRef(null);
  const menuRef = useRef(null);

  const [internalState, setInternalState] = useState({
    selectedIndex: null
  });

  const commandEvent = useEffectEvent(command);
  const selectItem = useCallback((index) => {
    if (index >= items.length) {
      return;
    }

    commandEvent?.({
      id: items[index].value?.toString(),
      label: items[index].display
    });
  }, [items, commandEvent]);

  const count = items.length ?? 0;
  const mentionList = useMemo(() => {
    return {
      refs: {
        ref: innerRef,
        menuRef: menuRef
      },
      state: {
        ...internalState,
        ...utils.cleanObject({open})
      },
      onKeyDown: ({event}) => {
        if (event.key === 'Escape') {
          innerRef.current?.close?.();
          return true;
        }

        if (event.key === 'ArrowUp') {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              selectedIndex: (current.selectedIndex + count - 1) % count
            })(current);
          });
          return true;
        }

        if (event.key === 'ArrowDown') {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              selectedIndex: (current.selectedIndex + 1) % count
            })(current);
          });

          return true;
        }

        if (event.key === 'Enter') {
          selectItem(internalState.selectedIndex);
          return true;
        }

        return false;
      },
      selectItem: (index) => {
        selectItem(index);
      }
    }
  }, [internalState, selectItem, count, open]);

  useImperativeHandle(ref, () => mentionList);

  useEffect(() => {
    setInternalState(utils.updater({selectedIndex: 0, open: true}, true));
  }, [items]);

  const onItemClick = (idx) => () => {
    selectItem(idx);
  }

  const handleKeyDown = (e) => {
    mentionList.onKeyDown({event: e});
  }

  const handleToggle = (open) => {
    setInternalState(utils.updater({open}, true));
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledMentionList ref={innerRef} {...innerProps}
                            onToggle={handleToggle}
                            open={mentionList.state.open}>
    <MenuList ref={menuRef}
              onKeyDown={handleKeyDown}
              density="dense">
      {items?.map((item, idx) => {
        return <MenuItem key={idx}
                         data-suggestion-index={idx}
                         onClick={item.onClick ?? onItemClick(idx)}
                         onMouseEnter={item.onMouseEnter}
                         selected={item.selected ?? (idx === mentionList.state.selectedIndex)}>
          {item.label}
        </MenuItem>
      })}
    </MenuList>
  </StyledMentionList>
});

MentionList.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  items: PropTypes.array,
  command: PropTypes.func,
  open: PropTypes.bool
};

export default MentionList;
